import {asyncSleep} from '@Utils/misc.util';
import {tryStreamPollingFastPath} from './polling-ajax.library';
import type {
  PollableJobConfig, PollEnvelope, PollKickoffAck, PollTransportParams, ResumePollableJobConfig,
} from './pollable-job.library.types';
import {
  MALFORMED_DONE_CODE, PollError, PollFailError, PollState, PollTimeoutError, POLL_FALLBACK_PAUSE_MS, UNKNOWN_STATE_CODE,
} from './pollable-job.library.types';

/** Sentinel returned by {@link interpret} when the envelope is non-terminal. */
export const POLL_PROCESSING = Symbol('poll-processing');

function assertHasPollingKey(ack: PollKickoffAck): void {
  if (typeof ack.pollingKey !== 'string' || ack.pollingKey.length === 0) {
    throw new Error('pollableJob: kickoff must resolve an ack with a non-empty string pollingKey');
  }
}

/** Single interpretation shared by the SSE fast-path and the long-poll fallback. */
export function interpret<R>(env: PollEnvelope<R>): R | typeof POLL_PROCESSING {
  if (env.state === PollState.PROCESSING) {
    return POLL_PROCESSING;
  }
  if (env.state === PollState.FAIL) {
    throw new PollFailError(env.error ?? '', env.data);
  }
  if (env.state === PollState.ERROR) {
    throw new PollError(env.error);
  }
  if (env.state === PollState.DONE) {
    if (env.data === undefined) {
      throw new PollError(MALFORMED_DONE_CODE);
    }
    return env.data;
  }
  throw new PollError(UNKNOWN_STATE_CODE);
}

function isTerminal(state: PollState): boolean {
  return state !== PollState.PROCESSING;
}

/**
 * Shared observation path for both a fresh kickoff and a resumed join: SSE fast path
 * (reusing tryStreamPollingFastPath, adapting the {state} envelope to its
 * {isAvailable,data} arms), then a long-poll fallback within one wall-clock deadline
 * computed from `timeoutMs`. Resolves R on done; throws PollFailError/PollError; throws
 * PollTimeoutError on budget elapse.
 */
async function observeJob<R>(
  status: (ack: PollKickoffAck, transportParams: PollTransportParams) => Promise<PollEnvelope<R>>,
  ack: PollKickoffAck,
  timeoutMs: number,
  eventId: string | null
): Promise<R> {
  const deadline = Date.now() + timeoutMs;

  if (window.PMW.streamGatewayEnabled) {
    const fast = await tryStreamPollingFastPath<PollEnvelope<R>>(
      ack,
      async (_d: unknown, immediate?: boolean): Promise<{isAvailable: boolean; data: PollEnvelope<R>}> => {
        const env = await status(ack, immediate ? {streamImmediate: 1} : {});
        return {isAvailable: isTerminal(env.state), data: env};
      },
      {timeout: timeoutMs, eventId}
    );
    if (fast.handled) {
      const outcome = interpret(fast.result);
      if (outcome !== POLL_PROCESSING) {
        return outcome;
      }
    }
  }

  return pollFallbackStep(status, ack, deadline);
}

/**
 * Drives an async job to a typed result from a fresh kickoff. See {@link observeJob}
 * for the shared SSE/fallback observation semantics.
 */
export async function pollableJob<R>(config: PollableJobConfig<R>): Promise<R> {
  const ack = await config.kickoff();
  assertHasPollingKey(ack);
  config.onAck?.(ack);
  if (ack.state !== undefined) {
    const outcome = interpret<R>({state: ack.state, data: ack.data as R | undefined, error: ack.error});
    if (outcome !== POLL_PROCESSING) {
      return outcome;
    }
  }
  return observeJob(config.status, ack, config.timeoutMs, config.eventId ?? null);
}

/**
 * Join an existing job by its persisted pollingKey without kicking anything off.
 * Never mutates server state: safe to call on poster open / editor reload.
 */
export async function resumePollableJob<R>(config: ResumePollableJobConfig<R>): Promise<R> {
  return observeJob(config.status, {pollingKey: config.pollingKey}, config.timeoutMs, config.eventId ?? null);
}

async function pollFallbackStep<R>(
  status: (ack: PollKickoffAck, transportParams: PollTransportParams) => Promise<PollEnvelope<R>>,
  ack: PollKickoffAck,
  deadline: number
): Promise<R> {
  if (Date.now() >= deadline) {
    throw new PollTimeoutError(ack.pollingKey);
  }
  const env = await status(ack, {});
  const outcome = interpret(env);
  if (outcome !== POLL_PROCESSING) {
    return outcome;
  }
  await asyncSleep(POLL_FALLBACK_PAUSE_MS);
  return pollFallbackStep(status, ack, deadline);
}
